import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutlined, WhatsApp } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../../components/SectionContainer';
import RevealOnScroll from '../../components/RevealOnScroll';
import { submitInstituteLead } from '../leadService';
import useAnalytics from '../../../analytics/useAnalytics';
import { EVT_INSTITUTE_MARKETING_SUBMIT } from '../../../analytics/events';
import { INDIAN_CITIES } from '../../../utils/indianCities';

const PRIMARY_COURSES_OPTIONS = [
  'JEE',
  'NEET',
  'UPSC',
  'SSC / CGL',
  'Banking',
  'CAT / MBA',
  'GATE',
  'CLAT',
  'CA / CS',
  'Board Exams',
  'Other',
];

const TRUST_BULLETS = [
  'Free to list',
  '48-hour onboarding',
  'Real-time lead alerts',
];

const PHONE_RE = /^[6-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM = {
  ownerName: '',
  phone: '',
  email: '',
  instituteName: '',
  city: null,
  primaryCourses: [],
  currentStudents: '',
  message: '',
};

const EMPTY_ERRORS = {
  ownerName: '',
  phone: '',
  email: '',
  instituteName: '',
  city: '',
  primaryCourses: '',
};

function validate(fields) {
  const errs = { ...EMPTY_ERRORS };
  if (!fields.ownerName || fields.ownerName.trim().length < 2) {
    errs.ownerName = 'Name must be at least 2 characters.';
  } else if (fields.ownerName.trim().length > 100) {
    errs.ownerName = 'Name must be under 100 characters.';
  }
  if (!fields.phone || !PHONE_RE.test(fields.phone)) {
    errs.phone = 'Enter a valid 10-digit Indian mobile number.';
  }
  if (!fields.email || !EMAIL_RE.test(fields.email)) {
    errs.email = 'Enter a valid email address.';
  }
  if (!fields.instituteName || fields.instituteName.trim().length < 2) {
    errs.instituteName = 'Institute name must be at least 2 characters.';
  } else if (fields.instituteName.trim().length > 200) {
    errs.instituteName = 'Institute name must be under 200 characters.';
  }
  if (!fields.city) {
    errs.city = 'Please select a city.';
  }
  if (!fields.primaryCourses || fields.primaryCourses.length === 0) {
    errs.primaryCourses = 'Select at least one course.';
  }
  return errs;
}

function hasErrors(errs) {
  return Object.values(errs).some(Boolean);
}

function SignupSection() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { track } = useAnalytics();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [submitting, setSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.6)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(27,54,93,0.08)';

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (hasErrors(errs)) return;

    setSubmitting(true);
    const payload = {
      ownerName: form.ownerName.trim(),
      phone: form.phone,
      email: form.email,
      instituteName: form.instituteName.trim(),
      city: form.city,
      primaryCourses: form.primaryCourses,
      currentStudents: form.currentStudents ? Number(form.currentStudents) : undefined,
      message: form.message.trim(),
    };
    await submitInstituteLead(payload);
    track(EVT_INSTITUTE_MARKETING_SUBMIT, { city: payload.city });
    setSubmitting(false);
    setSnackOpen(true);
    setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
  };

  return (
    <SectionContainer
      id="signup"
      eyebrow="Get started"
      title="List your institute with BookMyLibrary."
      subtitle="Fill the form. Our team will call you within 24 hours."
      sx={{
        background: isDark
          ? `radial-gradient(ellipse at 80% 50%, rgba(242,101,34,0.08) 0%, transparent 70%), ${theme.palette.background.default}`
          : `radial-gradient(ellipse at 80% 50%, rgba(242,101,34,0.06) 0%, transparent 70%), ${theme.palette.background.default}`,
      }}
    >
      <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
        {/* Left: pitch column */}
        <Grid item xs={12} md={5}>
          <RevealOnScroll>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                Join coaching institutes growing with BML.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 4 }}>
                Our team will help you set up your complete profile — gallery, courses, batches —
                and you get your first lead within days, not weeks.
              </Typography>

              <Button
                variant="outlined"
                component="a"
                href="https://wa.me/918595675155?text=Hi%20BookMyLibrary%20team%2C%20I%20want%20to%20know%20more%20about%20listing%20my%20institute"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsApp />}
                sx={{
                  borderColor: '#25D366',
                  color: '#25D366',
                  fontWeight: 700,
                  borderWidth: 2,
                  mb: 4,
                  '&:hover': {
                    borderColor: '#1DA851',
                    color: '#1DA851',
                    background: 'rgba(37,211,102,0.06)',
                    borderWidth: 2,
                  },
                }}
              >
                Chat with us on WhatsApp
              </Button>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {TRUST_BULLETS.map((bullet) => (
                  <Box key={bullet} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleOutlined sx={{ color: orange, fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {bullet}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </RevealOnScroll>
        </Grid>

        {/* Right: form card */}
        <Grid item xs={12} md={7}>
          <RevealOnScroll delay={0.1}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '24px',
                background: cardBg,
                border: cardBorder,
                boxShadow: isDark
                  ? '0 8px 40px rgba(0,0,0,0.3)'
                  : '0 8px 40px rgba(27,54,93,0.1)',
              }}
            >
              <Grid container spacing={2.5}>
                {/* Director / Owner name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Director / Owner name"
                    value={form.ownerName}
                    onChange={set('ownerName')}
                    error={Boolean(errors.ownerName)}
                    helperText={errors.ownerName}
                    fullWidth
                    required
                    inputProps={{ 'aria-label': 'Director or owner name' }}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Mobile number"
                    value={form.phone}
                    onChange={set('phone')}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    fullWidth
                    required
                    inputProps={{
                      inputMode: 'numeric',
                      maxLength: 10,
                      'aria-label': 'Mobile number',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">+91</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    label="Email address"
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    fullWidth
                    required
                    inputProps={{ 'aria-label': 'Email address' }}
                  />
                </Grid>

                {/* Institute name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Institute name"
                    value={form.instituteName}
                    onChange={set('instituteName')}
                    error={Boolean(errors.instituteName)}
                    helperText={errors.instituteName}
                    fullWidth
                    required
                    inputProps={{ 'aria-label': 'Institute name' }}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={INDIAN_CITIES}
                    value={form.city}
                    onChange={(_e, val) =>
                      setForm((prev) => ({ ...prev, city: val }))
                    }
                    freeSolo={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        required
                        error={Boolean(errors.city)}
                        helperText={errors.city}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'City',
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Primary courses */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={PRIMARY_COURSES_OPTIONS}
                    value={form.primaryCourses}
                    onChange={(_e, val) =>
                      setForm((prev) => ({ ...prev, primaryCourses: val }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Primary courses offered"
                        required
                        error={Boolean(errors.primaryCourses)}
                        helperText={errors.primaryCourses || 'Select all that apply'}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Primary courses offered',
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Current students (optional) */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Current students (optional)"
                    type="number"
                    value={form.currentStudents}
                    onChange={set('currentStudents')}
                    fullWidth
                    inputProps={{ min: 0, max: 10000, 'aria-label': 'Current students' }}
                  />
                </Grid>

                {/* Optional message */}
                <Grid item xs={12} sm={6}>
                  {/* intentionally blank to keep grid aligned */}
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <TextField
                    label="Anything else you'd like to share (optional)"
                    multiline
                    rows={3}
                    value={form.message}
                    onChange={set('message')}
                    fullWidth
                    inputProps={{ maxLength: 500, 'aria-label': 'Additional message' }}
                    helperText={`${form.message.length}/500`}
                  />
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    sx={{
                      background: `linear-gradient(135deg, ${orange}, ${golden})`,
                      color: '#fff',
                      fontWeight: 700,
                      py: 1.75,
                      fontSize: '1rem',
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                        boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
                        transform: submitting ? 'none' : 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                    startIcon={
                      submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : null
                    }
                  >
                    {submitting ? 'Submitting…' : 'Submit details'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </RevealOnScroll>
        </Grid>
      </Grid>

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          We received your details. Our team will call you within 24 hours.
        </Alert>
      </Snackbar>
    </SectionContainer>
  );
}

export default SignupSection;
